import React, { useMemo } from 'react';

// DEPENDENCIES
import lowerCase from 'lodash/lowerCase';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { compose } from 'redux';

// GLOBAL VARIABLES
import { INTERFACE, RECORD } from 'pathnames.js';
import { RETAIL_WHOLESALE, WHOLESALE_FORMAT } from 'codes';

// HOOKS/HELPERS
import { getEpostData } from 'helpers/getEpostData';
import { viewRedirect } from 'helpers/viewRedirect';

// FUNCTIONS
import { checkRole, makePath } from 'functions.js';

// PARTS
import SalesButtons from '../tools/SalesButtons';

// LOCAL COMPONENTS
import SalesDetail from '../parts/Detail';
import RenderDealer from './parts/Dealer';
import RenderEmployee from './parts/EmployeeInfo/EmployeeInfo';
import RenderInternalDeal from '../parts/InternalDealRecap';
import RenderVehicleRegistration from './parts/VehicleRegistration';
import RenderVehicle from './parts/VehicleInfo';
import RenderInventoryInternal from '../parts/InventoryInternal';
import RenderPurchaseInfo from './parts/PurchaseInfo';
import RenderCustomerInfo from './parts/CustomerInfo';
import RenderStatus from './parts/Status/Status';
import PricingInfoForm from '../parts/PricingInfo';
import DownPaymentInfo from './parts/DownPayment';
import DealerPackInfo from './parts/DealerPack';
import DealerIncentivesInfo from './parts/DealerIncentives';
import SpotInstructionsInfo from './parts/SpotInstructions';
import TradeInfo from './parts/Trade';
import RebateInfo from './parts/Rebate';
import MiscItems from './parts/MiscItems';
import DealCompletionInfo from './parts/DealCheckList';
import AfterFinaceInfo from './parts/AftermarketFinance';
import SaleAttachment from './parts/SaleAttachments';
import CarryoverSidebar from '../tools/CarryoverSidebar';
import BackoutSidebar from '../tools/BackoutSidebar';
import EpostSidebar from '../tools/EpostSidebar';
import RebillEpostSidebar from '../../deliveryLog/parts/RebillEpostSidebar';

// COMPONENT
const SalesViewSections = compose(
  viewRedirect
)(() => {
  const { values: {
    registration_address1,
    sales_status_name,
    sale_type_category_code,
    wholesale_format_id
  } = {} } = useFormikContext() || {};

  const isSold = useMemo(() => lowerCase(sales_status_name) === 'sold', [sales_status_name]);
  const isWholesale = sale_type_category_code === RETAIL_WHOLESALE.wholesale.category_code;
  const isWholesaleSimplified = isWholesale && wholesale_format_id === WHOLESALE_FORMAT.simplified.numeric;

  return (
    <>
      <RenderDealer />
      <RenderStatus />
      <RenderCustomerInfo />
      {!!registration_address1 && <RenderVehicleRegistration />}
      {!isWholesaleSimplified && <RenderEmployee />}
      <RenderVehicle />
      <PricingInfoForm />
      <RenderInventoryInternal />
      <RenderPurchaseInfo />
      <TradeInfo />
      <RebateInfo />
      <MiscItems />
      <DealerIncentivesInfo />
      <DealerPackInfo />
      {!isSold && <AfterFinaceInfo />}
      <DownPaymentInfo />
      <RenderInternalDeal />
      <SpotInstructionsInfo />
      <SaleAttachment />
      <DealCompletionInfo />
    </>
  );
});

// MAIN COMPONENT
const SalesView = props => {

  // REACT ROUTER
  const navigate = useNavigate();
  const { recordID } = useParams();

  // VARIABLES
  const roleCheck = checkRole(['dslAdmin', 'salesManager', 'salesManagerPlus']);

  // FUNCTIONS
  const goToEdit = roleCheck ? () => navigate(makePath(INTERFACE.sales, RECORD.edit, recordID)) : undefined;

  // RENDER
  return (<>
    <SalesDetail
      title="Deal Information"
      onEditClick={goToEdit}
      buttons={<SalesButtons />}
    >
      <SalesViewSections {...props} />
      <CarryoverSidebar />
      <BackoutSidebar />
      <EpostSidebar />
      <RebillEpostSidebar.Record />
    </SalesDetail>
  </>);
}

// EXPORT
export default getEpostData(SalesView);
